import type { SupabaseClient } from "@supabase/supabase-js";

// Client contact profile data layer (CONSOLE-CONTACT-PROFILES). Reads use explicit
// PII-minimal column allowlists; writes go ONLY through the audited hosted RPCs from
// 20260613030201. No direct table mutations from the console.

export type ContactMethodType = "email" | "mobile" | "office_phone" | "sms" | "voice" | "other";
export type ContactAssignmentRole =
  | "primary"
  | "operations"
  | "billing"
  | "on_site"
  | "emergency"
  | "after_hours"
  | "facilities_access"
  | "supervisor_escalation";

export interface ClientContact {
  contactId: string;
  displayName: string;
  organization: string | null;
  title: string | null;
  notes: string | null;
  active: boolean;
}

export interface ContactMethod {
  methodId: string;
  contactId: string;
  methodType: ContactMethodType;
  methodValue: string;
  label: string | null;
  priority: number;
  alertCapable: boolean;
  active: boolean;
}

export interface ContactAssignment {
  assignmentId: string;
  contactId: string;
  clientId: string | null;
  siteId: string | null;
  role: ContactAssignmentRole;
  priority: number;
  visibleToGuards: boolean;
  alertRecipient: boolean;
  notes: string | null;
  active: boolean;
}

export interface ClientContactsReadModel {
  contacts: ClientContact[];
  methods: ContactMethod[];
  assignments: ContactAssignment[];
}

export interface ContactDraft {
  displayName: string;
  organization: string | null;
  title: string | null;
  notes: string | null;
}

export interface ContactMethodDraft {
  methodType: ContactMethodType;
  methodValue: string;
  label: string | null;
  priority: number;
  alertCapable: boolean;
}

export interface ContactAssignmentDraft {
  contactId: string;
  clientId: string | null;
  siteId: string | null;
  role: ContactAssignmentRole;
  priority: number;
  visibleToGuards: boolean;
  alertRecipient: boolean;
  notes: string | null;
}

export const CONTACT_COLUMNS = "id, display_name, organization, title, notes, active";
export const CONTACT_METHOD_COLUMNS =
  "id, contact_id, method_type, method_value, label, priority, alert_capable, active";
export const CONTACT_ASSIGNMENT_COLUMNS =
  "id, contact_id, client_id, site_id, role, priority, visible_to_guards, alert_recipient, notes, active";

const LIST_LIMIT = 500;

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asNumber(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 1;
}

function asMethodType(value: unknown): ContactMethodType {
  const candidate = asString(value);
  if (
    candidate === "email" ||
    candidate === "mobile" ||
    candidate === "office_phone" ||
    candidate === "sms" ||
    candidate === "voice" ||
    candidate === "other"
  ) {
    return candidate;
  }
  return "other";
}

function asAssignmentRole(value: unknown): ContactAssignmentRole {
  const candidate = asString(value);
  if (
    candidate === "primary" ||
    candidate === "operations" ||
    candidate === "billing" ||
    candidate === "on_site" ||
    candidate === "emergency" ||
    candidate === "after_hours" ||
    candidate === "facilities_access" ||
    candidate === "supervisor_escalation"
  ) {
    return candidate;
  }
  return "operations";
}

/** Contacts + methods + assignments for the account. Throws safe. */
export async function fetchClientContacts(
  client: SupabaseClient,
  accountId: string,
): Promise<ClientContactsReadModel> {
  const [contactsResult, methodsResult, assignmentsResult] = await Promise.all([
    client.from("contacts").select(CONTACT_COLUMNS).eq("account_id", accountId).order("display_name", { ascending: true }).limit(LIST_LIMIT),
    client
      .from("contact_methods")
      .select(CONTACT_METHOD_COLUMNS)
      .eq("account_id", accountId)
      .order("priority", { ascending: true })
      .limit(LIST_LIMIT),
    client
      .from("contact_assignments")
      .select(CONTACT_ASSIGNMENT_COLUMNS)
      .eq("account_id", accountId)
      .order("priority", { ascending: true })
      .limit(LIST_LIMIT),
  ]);

  if (
    contactsResult.error !== null ||
    methodsResult.error !== null ||
    assignmentsResult.error !== null ||
    !Array.isArray(contactsResult.data) ||
    !Array.isArray(methodsResult.data) ||
    !Array.isArray(assignmentsResult.data)
  ) {
    throw new Error("Could not load client contacts.");
  }

  const contacts: ClientContact[] = [];
  for (const raw of contactsResult.data as Array<Record<string, unknown>>) {
    const contactId = asString(raw.id);
    if (contactId === null) continue;
    contacts.push({
      contactId,
      displayName: asString(raw.display_name) ?? "Contact",
      organization: asString(raw.organization),
      title: asString(raw.title),
      notes: asString(raw.notes),
      active: raw.active === true,
    });
  }

  const methods: ContactMethod[] = [];
  for (const raw of methodsResult.data as Array<Record<string, unknown>>) {
    const methodId = asString(raw.id);
    const contactId = asString(raw.contact_id);
    const methodValue = asString(raw.method_value);
    if (methodId === null || contactId === null || methodValue === null) continue;
    methods.push({
      methodId,
      contactId,
      methodType: asMethodType(raw.method_type),
      methodValue,
      label: asString(raw.label),
      priority: asNumber(raw.priority),
      alertCapable: raw.alert_capable === true,
      active: raw.active === true,
    });
  }

  const assignments: ContactAssignment[] = [];
  for (const raw of assignmentsResult.data as Array<Record<string, unknown>>) {
    const assignmentId = asString(raw.id);
    const contactId = asString(raw.contact_id);
    if (assignmentId === null || contactId === null) continue;
    assignments.push({
      assignmentId,
      contactId,
      clientId: asString(raw.client_id),
      siteId: asString(raw.site_id),
      role: asAssignmentRole(raw.role),
      priority: asNumber(raw.priority),
      visibleToGuards: raw.visible_to_guards === true,
      alertRecipient: raw.alert_recipient === true,
      notes: asString(raw.notes),
      active: raw.active === true,
    });
  }

  contacts.sort((a, b) => a.displayName.localeCompare(b.displayName));
  methods.sort((a, b) => a.priority - b.priority || a.methodValue.localeCompare(b.methodValue));
  assignments.sort((a, b) => a.priority - b.priority || a.role.localeCompare(b.role));
  return { contacts, methods, assignments };
}

export const SAFE_CONTACT_WRITE_FAILURE = "Couldn't save that contact change - please try again.";

const KNOWN_CONTACT_FAILURES: ReadonlySet<string> = new Set([
  "Authentication is required to manage client profiles.",
  "Account id is required to manage client profiles.",
  "Caller is not authorized to manage client profiles for this account.",
  "Contact display name is required.",
  "Contact external refs must be a JSON object.",
  "Contact id is required.",
  "Active contact was not found for this account.",
  "Archived contacts cannot be updated.",
  "Contact method id is required.",
  "Contact method type is required.",
  "Contact method value is required.",
  "Active contact method was not found for this account.",
  "Archived contact methods cannot be updated.",
  "Contact assignment id is required.",
  "Contact assignment must target exactly one client or site.",
  "Client was not found for this account.",
  "Site was not found for this account.",
  "Contact assignment role is required.",
  "Active contact assignment was not found for this account.",
  "Archived contact assignments cannot be updated.",
]);

export type ContactWriteOutcome =
  | { ok: true; contactId: string | null; contactMethodId: string | null; contactAssignmentId: string | null }
  | { ok: false; reason: string };

async function runContactWrite(
  client: SupabaseClient,
  fn: string,
  params: Record<string, unknown>,
): Promise<ContactWriteOutcome> {
  try {
    const { data, error } = await client.rpc(fn, params);
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return { ok: false, reason: KNOWN_CONTACT_FAILURES.has(message) ? message : SAFE_CONTACT_WRITE_FAILURE };
    }
    const row = Array.isArray(data) ? (data[0] as Record<string, unknown> | undefined) : undefined;
    return {
      ok: true,
      contactId: typeof row?.contact_id === "string" ? row.contact_id : null,
      contactMethodId: typeof row?.contact_method_id === "string" ? row.contact_method_id : null,
      contactAssignmentId: typeof row?.contact_assignment_id === "string" ? row.contact_assignment_id : null,
    };
  } catch {
    return { ok: false, reason: SAFE_CONTACT_WRITE_FAILURE };
  }
}

function contactParams(draft: ContactDraft): Record<string, unknown> {
  return {
    p_display_name: draft.displayName,
    p_organization: draft.organization,
    p_title: draft.title,
    p_notes: draft.notes,
    p_external_refs: {},
  };
}

export async function createContact(
  client: SupabaseClient,
  accountId: string,
  draft: ContactDraft,
): Promise<ContactWriteOutcome> {
  return runContactWrite(client, "create_contact", { p_account_id: accountId, ...contactParams(draft) });
}

export async function updateContact(
  client: SupabaseClient,
  accountId: string,
  contactId: string,
  draft: ContactDraft,
): Promise<ContactWriteOutcome> {
  return runContactWrite(client, "update_contact", {
    p_account_id: accountId,
    p_contact_id: contactId,
    ...contactParams(draft),
  });
}

export async function archiveContact(
  client: SupabaseClient,
  accountId: string,
  contactId: string,
): Promise<ContactWriteOutcome> {
  return runContactWrite(client, "archive_contact", { p_account_id: accountId, p_contact_id: contactId });
}

function methodParams(draft: ContactMethodDraft): Record<string, unknown> {
  return {
    p_method_type: draft.methodType,
    p_method_value: draft.methodValue,
    p_label: draft.label,
    p_priority: draft.priority,
    p_alert_capable: draft.alertCapable,
  };
}

export async function createContactMethod(
  client: SupabaseClient,
  accountId: string,
  contactId: string,
  draft: ContactMethodDraft,
): Promise<ContactWriteOutcome> {
  return runContactWrite(client, "create_contact_method", {
    p_account_id: accountId,
    p_contact_id: contactId,
    ...methodParams(draft),
  });
}

export async function updateContactMethod(
  client: SupabaseClient,
  accountId: string,
  contactMethodId: string,
  contactId: string,
  draft: ContactMethodDraft,
): Promise<ContactWriteOutcome> {
  return runContactWrite(client, "update_contact_method", {
    p_account_id: accountId,
    p_contact_method_id: contactMethodId,
    p_contact_id: contactId,
    ...methodParams(draft),
  });
}

export async function archiveContactMethod(
  client: SupabaseClient,
  accountId: string,
  contactMethodId: string,
): Promise<ContactWriteOutcome> {
  return runContactWrite(client, "archive_contact_method", {
    p_account_id: accountId,
    p_contact_method_id: contactMethodId,
  });
}

function assignmentParams(draft: ContactAssignmentDraft): Record<string, unknown> {
  return {
    p_contact_id: draft.contactId,
    p_client_id: draft.clientId,
    p_site_id: draft.siteId,
    p_role: draft.role,
    p_priority: draft.priority,
    p_visible_to_guards: draft.visibleToGuards,
    p_alert_recipient: draft.alertRecipient,
    p_notes: draft.notes,
  };
}

export async function assignContact(
  client: SupabaseClient,
  accountId: string,
  draft: ContactAssignmentDraft,
): Promise<ContactWriteOutcome> {
  return runContactWrite(client, "assign_contact", { p_account_id: accountId, ...assignmentParams(draft) });
}

export async function updateContactAssignment(
  client: SupabaseClient,
  accountId: string,
  contactAssignmentId: string,
  draft: ContactAssignmentDraft,
): Promise<ContactWriteOutcome> {
  return runContactWrite(client, "update_contact_assignment", {
    p_account_id: accountId,
    p_contact_assignment_id: contactAssignmentId,
    ...assignmentParams(draft),
  });
}

export async function archiveContactAssignment(
  client: SupabaseClient,
  accountId: string,
  contactAssignmentId: string,
): Promise<ContactWriteOutcome> {
  return runContactWrite(client, "archive_contact_assignment", {
    p_account_id: accountId,
    p_contact_assignment_id: contactAssignmentId,
  });
}
